import { cn } from "@/lib/utils/ui";
import { useDraggable } from "@dnd-kit/core";
import clsx from "clsx";
import { motion } from "framer-motion";
import {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
  type FocusEvent,
} from "react";
import { IoSwapHorizontal } from "react-icons/io5";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";
import { Card, CardTitle, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Exercise, ExerciseSet, TrackingType } from "./workoutsColumns";
import ExerciseHistoryPopover from "./ExerciseHistoryPopover";
import {
  MoreHorizontal,
  NotepadText,
  Plus,
  Trash2,
  Unlink,
  X,
} from "lucide-react";
import { Label } from "../ui/label";
import { v4 as uuidv4 } from "uuid";
import { useOrg } from "@/lib/states/session/hooks";
import InlineTooltip from "../inlineTooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import {
  DistanceUnit,
  getDistanceDisplay,
  metersToDistanceDisplay,
  distanceDisplayToMeters,
} from "@/lib/utils/exerciseTracking";

const COACH_NOTES_MAX_HEIGHT_PX = 144;

interface ExerciseRowProps {
  exercise: Exercise;
  readOnly?: boolean;
  groupLength: number;
  canShowLink: boolean;
  dragId?: string;
  dragData?: Record<string, unknown>;
  dragDisabled?: boolean;
  isRecentlyDropped?: boolean;
  clientId?: string;
  weightUnitLabel?: string;
  distanceUnitLabel?: DistanceUnit;
  updateExercise: (
    exercise: Exercise,
    ex_order: number,
    sub_order: number,
  ) => void;
  removeExercise: (
    exercise: Exercise,
    ex_order: number,
    sub_order: number,
  ) => void;
  replaceExercise: (exercise: Exercise) => void;
  removeFromSuperset: (
    exercise: Exercise,
    ex_order: number,
    sub_order: number,
  ) => void;
  exerciseSelected: (exercise: Exercise) => void;
}

const ExerciseRow: FunctionComponent<ExerciseRowProps> = ({
  exercise,
  readOnly = false,
  updateExercise,
  removeExercise,
  replaceExercise,
  removeFromSuperset,
  groupLength,
  exerciseSelected,
  canShowLink,
  dragId,
  dragData,
  dragDisabled = false,
  isRecentlyDropped = false,
  clientId,
  weightUnitLabel,
  distanceUnitLabel,
}) => {
  const [showCoachCueEditor, setShowCoachCueEditor] = useState(false);
  const coachNotesRef = useRef<HTMLTextAreaElement | null>(null);
  const org = useOrg();
  const hasCoachCue = Boolean((exercise.coach_note ?? "").trim());
  const distanceUnit: DistanceUnit =
    org?.distanceUnitDefault === "mi" ? "mi" : "km";
  const trackingType = (exercise.tracking_type ?? "strength") as TrackingType;
  const isDistance = trackingType.startsWith("distance");
  const isTimed = trackingType === "timed";
  const distanceDisplay = getDistanceDisplay(trackingType, distanceUnit);

  const draggableId = dragId ?? `exercise-static-${exercise.sortableId}`;
  const isDragDisabled = readOnly || dragDisabled || !dragId;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: draggableId,
    data: dragData,
    disabled: isDragDisabled,
  });
  const blockDragPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };
  const interactiveProps = {
    onPointerDown: blockDragPropagation,
    onMouseDown: blockDragPropagation,
    onTouchStart: blockDragPropagation,
  };

  const updateSet = (id: string, updates: Partial<ExerciseSet>) => {
    const newExercise = exercise;
    newExercise.sets = newExercise.sets.map((el) =>
      el.id === id ? { ...el, ...updates } : el,
    ) as ExerciseSet[];
    updateExercise(newExercise, exercise.ex_order, exercise.sub_order);
  };

  const handleRangeChange =
    (id: string, field: "startRange" | "endRange") => (event: any) => {
      const { value, valueAsNumber } = event.target;
      if (value !== "" && isNaN(valueAsNumber)) {
        return;
      }
      updateSet(id, {
        [field]: value === "" ? undefined : valueAsNumber,
      });
    };

  const formatTimeInput = (seconds?: number | null) => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
      return "";
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const parseTimeDigitsToSeconds = (value: string) => {
    const digits = value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) return null;
    const minutesPart = digits.length > 2 ? digits.slice(0, -2) : "";
    const secondsPart = digits.slice(-2);
    const minutes = minutesPart ? Number(minutesPart) : 0;
    const seconds = Number(secondsPart);
    if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return minutes * 60 + seconds;
  };

  const selectAllOnFocus = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    event.currentTarget.select();
  };

  const handleTimeChange = (id: string) => (event: any) => {
    const { value } = event.target;
    if (value === "") {
      updateSet(id, { targetTimeSeconds: undefined });
      return;
    }
    const parsed = parseTimeDigitsToSeconds(value);
    if (parsed === null) {
      updateSet(id, { targetTimeSeconds: undefined });
      return;
    }
    updateSet(id, { targetTimeSeconds: parsed === 0 ? undefined : parsed });
  };

  const handleTimeKeyDown =
    (id: string, currentSeconds?: number | null) =>
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
        return;
      }
      event.preventDefault();
      const delta = event.key === "ArrowUp" ? 1 : -1;
      const next = Math.max(0, Math.floor((currentSeconds ?? 0) + delta));
      updateSet(id, {
        targetTimeSeconds: next === 0 ? undefined : next,
      });
    };

  const handleDistanceChange = (id: string) => (event: any) => {
    const { value, valueAsNumber } = event.target;
    if (value === "") {
      updateSet(id, { targetDistanceMeters: undefined });
      return;
    }
    if (isNaN(valueAsNumber)) {
      return;
    }
    const meters = distanceDisplayToMeters(
      valueAsNumber,
      trackingType,
      distanceUnit,
    );
    updateSet(id, { targetDistanceMeters: meters });
  };

  const handleTargetWeightChange = (id: string) => (event: any) => {
    const { value, valueAsNumber } = event.target;
    if (value === "") {
      updateSet(id, { targetWeight: undefined });
      return;
    }
    if (isNaN(valueAsNumber)) {
      return;
    }
    updateSet(id, { targetWeight: valueAsNumber });
  };

  const exerciseChangeHandler = (event: any) => {
    const { name, value, valueAsNumber, type } = event.target;

    if (type === "number" && isNaN(valueAsNumber) && value !== "") {
      return;
    }
    const v = type === "number" ? (value === "" ? null : valueAsNumber) : value;
    const newExercise = { ...exercise, [name]: v };
    updateExercise(newExercise, exercise.ex_order, exercise.sub_order);
  };

  const onTempoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const newExercise = exercise;

    // Format the tempo value with hyphens after each digit
    const formattedValue = value
      .replace(/[^0-9]/g, "") // Remove any non-digit characters
      .slice(0, 4) // Limit the value to 4 digits
      .split("")
      .join("-");

    newExercise.tempo = formattedValue;
    updateExercise(newExercise, exercise.ex_order, exercise.sub_order);
  };

  const removeSet = (id: string) => () => {
    const newExercise = exercise;
    newExercise.sets = newExercise.sets.filter((s) => s.id != id);
    updateExercise(newExercise, exercise.ex_order, exercise.sub_order);
  };

  const newSet = () => {
    const newExercise = exercise;
    const sets = newExercise.sets;
    let set: ExerciseSet;
    if (sets.length > 0) {
      set = {
        ...sets[sets.length - 1],
        id: uuidv4(),
        set_type: sets[sets.length - 1].set_type ?? "working",
      };
    } else {
      if (isTimed) {
        set = {
          id: uuidv4(),
          set_type: "working",
        };
      } else if (isDistance) {
        set = {
          id: uuidv4(),
          set_type: "working",
        };
      } else {
        set = {
          startRange: 8,
          endRange: 10,
          id: uuidv4(),
          set_type: "working",
        };
        if (trackingType === "strength") {
          set.targetWeight = 0;
        }
      }
    }

    newExercise.sets = [...sets, set];

    console.log(newExercise, exercise.ex_order, exercise.sub_order);
    updateExercise(newExercise, exercise.ex_order, exercise.sub_order);
  };

  const group = groupLength > 1;

  const label = useMemo(() => {
    return `${(exercise.ex_order + 10).toString(36).toUpperCase()}${group ? exercise.sub_order + 1 : ""}`;
  }, [exercise.ex_order, exercise.sub_order, group]);

  useEffect(() => {
    const textarea = coachNotesRef.current;

    if (!textarea || (!showCoachCueEditor && !hasCoachCue)) {
      return;
    }

    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      COACH_NOTES_MAX_HEIGHT_PX,
    )}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > COACH_NOTES_MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [exercise.coach_note, hasCoachCue, showCoachCueEditor]);

  return (
    <motion.div
      initial={false}
      animate={
        isRecentlyDropped
          ? { opacity: [0.45, 1] }
          : { opacity: isDragging ? 0.25 : 1 }
      }
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        ref={setNodeRef}
        data-workout-exercise-row
        data-workout-exercise-id={exercise.sortableId}
        onPointerDown={blockDragPropagation}
        onMouseDown={blockDragPropagation}
        onTouchStart={blockDragPropagation}
      >
        <Card
          {...(!isDragDisabled ? listeners : {})}
          {...(!isDragDisabled ? attributes : {})}
          className={cn(
            "w-full rounded-sm p-3 transition-[color,background-color,border-color,opacity,box-shadow] duration-200 select-none touch-manipulation",
            !isDragDisabled && "cursor-grab active:cursor-grabbing",
            isDragging &&
              "border-foreground/20 bg-muted/70 shadow-sm opacity-45",
            (!canShowLink || readOnly) && "mb-3",
          )}
        >
          <CardTitle className="flex justify-between text-base">
            <div className={cn("flex flex-row gap-2 items-center")}>
              <h1 className="text-sm">{label} </h1>
              <ExerciseHistoryPopover
                exercise={exercise}
                clientId={clientId}
                readOnly={readOnly}
                weightUnitLabel={weightUnitLabel}
                distanceUnitLabel={distanceUnitLabel}
                onExerciseSelected={() => exerciseSelected(exercise)}
              />
            </div>
            <ButtonGroup {...interactiveProps}>
              {group && !readOnly && (
                <InlineTooltip title="Remove from superset" aschild>
                  <Button
                    {...interactiveProps}
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      removeFromSuperset(
                        exercise,
                        exercise.ex_order,
                        exercise.sub_order,
                      )
                    }
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </InlineTooltip>
              )}

              {!readOnly ? (
                <>
                  <InlineTooltip title="Replace exercise" aschild>
                    <Button
                      {...interactiveProps}
                      variant="outline"
                      role="combobox"
                      size="icon"
                      onClick={() => replaceExercise(exercise)}
                    >
                      <IoSwapHorizontal className="w-4 h-4" />
                    </Button>
                  </InlineTooltip>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        {...interactiveProps}
                        variant="outline"
                        size="icon"
                      >
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          if (hasCoachCue) {
                            setShowCoachCueEditor(true);
                            return;
                          }
                          setShowCoachCueEditor((prev) => !prev);
                        }}
                      >
                        <NotepadText className="mr-2 h-4 w-4" />
                        {hasCoachCue
                          ? "Edit coach notes"
                          : showCoachCueEditor
                            ? "Hide coach notes"
                            : "Add coach notes"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          removeExercise(
                            exercise,
                            exercise.ex_order,
                            exercise.sub_order,
                          )
                        }
                        variant={"destructive"}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Exercise
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
            </ButtonGroup>
          </CardTitle>
          <CardContent className={clsx("my-1.5 px-0 py-0")}>
            <div>
              {showCoachCueEditor || hasCoachCue ? (
                <div className="mb-3">
                  <Label className="text-xs text-gray-500">COACH NOTES</Label>
                  <Textarea
                    {...interactiveProps}
                    ref={coachNotesRef}
                    name="coach_note"
                    placeholder="Add guidance for this exercise..."
                    className="mt-1 min-h-[80px] resize-none overflow-y-hidden border-0 bg-muted/60 text-base shadow-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 md:text-sm"
                    onChange={exerciseChangeHandler}
                    value={exercise.coach_note ?? ""}
                    disabled={readOnly}
                  />
                </div>
              ) : null}

              <div className="flex flex-row gap-2 items-end mb-1">
                <div>
                  <Label className="text-xs text-gray-500">TEMPO</Label>
                  <Input
                    {...interactiveProps}
                    name="tempo"
                    type="text"
                    placeholder="X-X-X-X"
                    className="mt-1 h-8 w-24 text-base md:w-20 md:text-sm"
                    onFocus={selectAllOnFocus}
                    onChange={onTempoChange}
                    value={exercise.tempo}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">RIR</Label>
                  <Input
                    {...interactiveProps}
                    name="rir"
                    type="number"
                    placeholder="0"
                    className="mt-1 h-8 w-16 text-base md:text-sm"
                    onFocus={selectAllOnFocus}
                    onChange={exerciseChangeHandler}
                    value={exercise.rir ?? undefined}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">REST</Label>
                  <Input
                    {...interactiveProps}
                    name="rest_time"
                    type="number"
                    placeholder="0"
                    className="mt-1 h-8 w-20 text-base md:text-sm"
                    onFocus={selectAllOnFocus}
                    onChange={exerciseChangeHandler}
                    value={exercise.rest_time ?? undefined}
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>

            <div className="my-1.5 flex flex-wrap items-center gap-3 py-1 text-xs text-gray-500 lg:flex-nowrap">
              <span className="text-center w-7">SET</span>
              <div className="flex flex-wrap flex-grow items-center gap-2 justify-center lg:flex-nowrap">
                {!isTimed && !isDistance && (
                  <>
                    <span className="text-center w-[104px]">REP RANGE</span>
                    {trackingType === "strength" && (
                      <span className="text-center w-20">WEIGHT</span>
                    )}
                  </>
                )}
                {isTimed && (
                  <span className="text-center w-24">TIME (MM:SS)</span>
                )}
                {isDistance && (
                  <>
                    <span className="text-center w-24">
                      DISTANCE ({distanceDisplay.label})
                    </span>
                    <span className="text-center w-24">TIME (MM:SS)</span>
                  </>
                )}
              </div>
              <span className="text-center w-8"></span>
            </div>
            {exercise.sets.map((set, index) => {
              const distanceValue =
                set.targetDistanceMeters !== undefined &&
                set.targetDistanceMeters !== null
                  ? Math.round(
                      metersToDistanceDisplay(
                        set.targetDistanceMeters,
                        trackingType,
                        distanceUnit,
                      ) * 100,
                    ) / 100
                  : "";
              return (
                <div
                  key={set.id}
                  className={cn(
                    "my-1 flex flex-wrap items-center gap-3 rounded-md py-1 lg:flex-nowrap",
                    index % 2 === 0 && "bg-secondary",
                  )}
                >
                  <span
                    className={cn(
                      "text-center p-1 size-7 bg-secondary border-1 rounded-md items-center flex justify-center font-medium text-sm",
                      index % 2 === 0 && "bg-background",
                    )}
                  >
                    {index + 1}
                  </span>
                  <div className="flex flex-wrap flex-grow items-center gap-2 justify-center lg:flex-nowrap">
                    {!isTimed && !isDistance && (
                      <>
                        <Input
                          {...interactiveProps}
                          name="startRange"
                          type="number"
                          inputMode="numeric"
                          value={set.startRange ?? ""}
                          onFocus={selectAllOnFocus}
                          onChange={handleRangeChange(set.id, "startRange")}
                          className="h-8 w-11 text-center text-base md:text-sm"
                          disabled={readOnly}
                        />
                        <span className="text-center text-sm">-</span>
                        <Input
                          {...interactiveProps}
                          name="endRange"
                          type="number"
                          inputMode="numeric"
                          value={set.endRange ?? ""}
                          onFocus={selectAllOnFocus}
                          onChange={handleRangeChange(set.id, "endRange")}
                          className="h-8 w-11 text-center text-base md:text-sm"
                          disabled={readOnly}
                        />
                        {trackingType === "strength" && (
                          <Input
                            {...interactiveProps}
                            name="targetWeight"
                            type="number"
                            inputMode="decimal"
                            step="0.1"
                            value={set.targetWeight ?? ""}
                            onFocus={selectAllOnFocus}
                            onChange={handleTargetWeightChange(set.id)}
                            className="h-8 w-20 text-center text-base md:text-sm"
                            disabled={readOnly}
                          />
                        )}
                      </>
                    )}
                    {isTimed && (
                      <Input
                        {...interactiveProps}
                        name="targetTimeSeconds"
                        type="text"
                        inputMode="numeric"
                        value={formatTimeInput(set.targetTimeSeconds)}
                        onFocus={selectAllOnFocus}
                        onChange={handleTimeChange(set.id)}
                        onKeyDown={handleTimeKeyDown(
                          set.id,
                          set.targetTimeSeconds,
                        )}
                        className="h-8 w-24 text-center text-base md:text-sm"
                        placeholder="00:00"
                        disabled={readOnly}
                      />
                    )}
                    {isDistance && (
                      <>
                        <Input
                          {...interactiveProps}
                          name="targetDistanceMeters"
                          type="number"
                          inputMode="numeric"
                          value={distanceValue}
                          onFocus={selectAllOnFocus}
                          onChange={handleDistanceChange(set.id)}
                          className="h-8 w-24 text-center text-base md:text-sm"
                          disabled={readOnly}
                        />
                        <Input
                          {...interactiveProps}
                          name="targetTimeSeconds"
                          type="text"
                          inputMode="numeric"
                          value={formatTimeInput(set.targetTimeSeconds)}
                          onFocus={selectAllOnFocus}
                          onChange={handleTimeChange(set.id)}
                          onKeyDown={handleTimeKeyDown(
                            set.id,
                            set.targetTimeSeconds,
                          )}
                          className="h-8 w-24 text-center text-base md:text-sm"
                          placeholder="00:00"
                          disabled={readOnly}
                        />
                      </>
                    )}
                  </div>
                  <Button
                    {...interactiveProps}
                    variant={"ghost"}
                    size="sm"
                    className={cn(
                      "h-8 w-8 p-0",
                      exercise.sets.length === 1 && "!opacity-0",
                    )}
                    onClick={removeSet(set.id)}
                    disabled={exercise.sets.length === 1 || readOnly}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}
            {exercise.sets.length === 0 && (
              <div className="my-1 flex flex-row items-center gap-3 rounded-md py-1">
                <span className="text-center flex-grow text-sm">No sets</span>
              </div>
            )}
            {!readOnly ? (
              <Button
                {...interactiveProps}
                variant={"outline"}
                onClick={newSet}
                className="w-full h-8 text-sm mt-2"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Set
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};

export default ExerciseRow;
